// 2) Third-party libraries
import { get } from "lodash-es";
import { all, call, fork, takeLatest } from "redux-saga/effects";

// 3) Local imports
// 3.1 Constants
import { API_NAME } from "../../config/config";
import { SUPPORT_TICKET_LISTS_PAGE } from "../../config/tableAndPageConstants";

// 3.3 Project-specific modules � components/other
import { post } from "src/api";

// 3.4 Utility functions / types
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { ApplicationState } from "../storeTypes";
import {
    attachSupportTicketRequestAction,
    getSupportTicketListsRequestAction,
    submitSupportTicketRequestAction,
} from "./supportTicketSlice";

 /**
 * Function calling the API for fetching the remittance advice data based on the given id.
 * @param param0
 */
function* handleSubmitSupportTicketRequest({ payload: sagaPayload }: any) {
    const errorMessage =
        'Error submitting the support ticket. Please try again later.';
    const {
        payload,
        callback
    } = sagaPayload;
    try { 
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/support/ticket/submit',
            options: {
                body: payload,
            }
        });

        const response: DynamicObject = yield body.json();
        if (callback) {
            const responseCallback = {
                IsSuccess: get(response, 'isSuccess'),
                Id: get(response, 'id'),
                FileURLLists: get(response, 'fileLists'),
            };

            callback(responseCallback);
        }
        
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (callback) {
            const errorCallback = {
                IsSuccess: false
            };
            callback(errorCallback);
        }
    }
}   

/**
 * Function calling the API for attaching file for support ticket.
 * @param param0
 */
function* handleAttachFileSupportTicketRequest({ payload: sagaPayload }: any) {
    const {
        payload,
        callback
    } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/attach-file/support/ticket/submit',
            options: {
                body: payload,
            }
        });

        const response: DynamicObject = yield body.json();
        if (callback) {
            const responseCallback = {
                IsSuccess: get(response, 'isSuccess'),
                Id: get(response, 'id'),
                FileURLLists: get(response, 'fileLists'),
            };

            callback(responseCallback);
        }
        
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (callback) {
            const errorCallback = {
                IsSuccess: false
            };
            callback(errorCallback);
        }
    }
}   

/**
 * Function called for getting support ticket lists
 */
function* handleGetSupportTicketListsRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    const errorMessage =
        ['Error fetching support ticket list. Please try again later.'];
    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/support/ticket/lists',
            options: { 
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const supportTickets = get(res,'data');
        if (supportTickets) {
            const responsePayload = {
                data: supportTickets,
                IsSuccess: true,
                pageData: {
                    pageSize: get(payload,'pageSize'),
                    currentPage: get(payload,'currentPage'),
                    hasNextPage:
                        !(supportTickets.length < get(payload,'pageSize')) &&
                        !(get(payload,'pageSize') < SUPPORT_TICKET_LISTS_PAGE.pageSize),
                },
            };

            callback(responsePayload);
        } else {
            const responsePayload = {
                IsSuccess: true,
                Messages: get(res,'Messages')
            };

            callback(responsePayload);
        }
    } catch (err) {
        if (callback) {
            const errorCallback = {
                IsSuccess: false,
                Messages: errorMessage
            };
            callback(errorCallback);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.

function* watchSubmitSupportTicketRequest() {
    yield takeLatest(
        submitSupportTicketRequestAction.type,
        handleSubmitSupportTicketRequest
    );
}

function* watchAttachFileSupportTicketRequest() {
    yield takeLatest(
        attachSupportTicketRequestAction.type,
        handleAttachFileSupportTicketRequest
    );
}

function* watchGetSupportTicketListsRequest() {
    yield takeLatest(
        getSupportTicketListsRequestAction.type,
        handleGetSupportTicketListsRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* supportTicketsSaga() {
    yield all([
        fork(watchSubmitSupportTicketRequest),
        fork(watchAttachFileSupportTicketRequest),
        fork(watchGetSupportTicketListsRequest)
    ]);
}

export default supportTicketsSaga;
